import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Chip, Paper, Stack, Typography} from 'views/components';
import {ArrowDownwardOutlined, ArrowUpwardOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

// Same card language as the Lead funnel key metrics, with two differences:
// values arrive pre-formatted (counts or rates) and `invertTrend` marks
// metrics where an increase is bad (fail rate, flagged, disagreements).
const MetricCardsSection = (props) => {
  const {
    isLoading,
    errorMessage,
    metricCards,
    missingDates,
    sectionTitleExpression,
  } = props;

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          lineHeight: '14px',
          letterSpacing: '0.04em',
          color: '#A8A49E',
          mb: 2,
        }}
      >
        <Translate expression={sectionTitleExpression} />
      </Typography>
      {
        isLoading &&
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))'},
            gap: 1.75,
          }}
        >
          {
            metricCards.map((metric) => (
              <Paper
                key={metric.label}
                sx={{
                  height: 128,
                  borderRadius: '8px',
                  border: '1px solid #E5E5E5',
                  boxShadow: 'none',
                  backgroundColor: '#F5F4F2',
                }}
              />
            ))
          }
        </Box>
      }
      {
        !isLoading &&
        errorMessage &&
        <Alert severity='warning'>
          {errorMessage}
        </Alert>
      }
      {
        !isLoading &&
        !errorMessage &&
        <Stack spacing={2}>
          {
            missingDates.length > 0 &&
            <Alert severity='info'>
              <Translate
                expression='metrics.AgentReportCard.MissingRollups'
                substitute={{dates: missingDates.join(', ')}}
              />
            </Alert>
          }
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))'},
              gap: 1.75,
            }}
          >
            {
              metricCards.map((metric) => {
                const maxValue = Math.max(1, ...metric.series);
                const isImprovement = metric.invertTrend ? metric.delta < 0 : metric.delta > 0;
                const isRegression = metric.invertTrend ? metric.delta > 0 : metric.delta < 0;
                const trendColor = isImprovement ? '#21892C' : isRegression ? '#DA7955' : '#919191';
                const trendBackgroundColor = isImprovement ? '#E3FAE5' : isRegression ? '#FFF1EC' : '#F2F0EC';
                const pointList = metric.series.map((value, index) => {
                  const x = metric.series.length === 1 ? 48 : (index / (metric.series.length - 1)) * 96;
                  const y = 32 - ((value / maxValue) * 28);

                  return `${x},${y}`;
                }).join(' ');
                const areaPoints = `${pointList} 96,32 0,32`;

                return (
                  <Paper
                    key={metric.label}
                    sx={{
                      p: 2.5,
                      borderRadius: '8px',
                      border: '1px solid #E5E5E5',
                      boxShadow: 'none',
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2}>
                        <Box sx={{minWidth: 0}}>
                          <Typography
                            sx={{
                              fontSize: '11px',
                              fontWeight: 600,
                              lineHeight: '14px',
                              letterSpacing: '0.04em',
                              color: '#A8A49E',
                              mb: 1.5,
                            }}
                          >
                            {metric.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '24px',
                              fontWeight: 600,
                              lineHeight: '31px',
                              color: '#1A1916',
                            }}
                          >
                            {metric.currentLabel}
                          </Typography>
                        </Box>
                        <Box
                          component='svg'
                          viewBox='0 0 96 32'
                          sx={{
                            width: 96,
                            height: 32,
                            flexShrink: 0,
                          }}
                        >
                          <polygon points={areaPoints} fill='rgba(218, 121, 85, 0.12)' />
                          <polyline
                            points={pointList}
                            fill='none'
                            stroke='#DA7955'
                            strokeWidth='2'
                            strokeLinejoin='round'
                            strokeLinecap='round'
                          />
                        </Box>
                      </Stack>
                      <Box sx={{borderTop: '1px solid #EAEAEA'}} />
                      <Stack direction='row' gap={1} alignItems='center'>
                        <Chip
                          icon={
                            metric.delta >= 0 ?
                              <ArrowUpwardOutlined sx={{fontSize: '14px !important', color: trendColor}} /> :
                              <ArrowDownwardOutlined sx={{fontSize: '14px !important', color: trendColor}} />
                          }
                          label={metric.deltaLabel}
                          sx={{
                            'height': 22,
                            'backgroundColor': trendBackgroundColor,
                            'borderRadius': '20px',
                            'color': trendColor,
                            'fontSize': '11px',
                            'fontWeight': 500,
                            '& .MuiChip-label': {
                              px: 1,
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '11px',
                            fontWeight: 500,
                            lineHeight: '14px',
                            color: '#A8A49E',
                          }}
                        >
                          {translate('metrics.AgentReportCard.KeyMetrics.Previous', {count: metric.previousLabel})}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })
            }
          </Box>
        </Stack>
      }
    </Box>
  );
};

MetricCardsSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
  metricCards: PropTypes.array.isRequired,
  missingDates: PropTypes.array.isRequired,
  sectionTitleExpression: PropTypes.string.isRequired,
};

export default React.memo(MetricCardsSection);
